import type { IconGlyph } from '../icon'
import type { ActionsWrapper, ActionsWrapperProps } from './actions-wrapper'
import type { ContentWrapper, ContentWrapperProps } from './content-wrapper'
import type { SupportingText, SupportingTextProps, Text, TextProps, TextsWrapper, TextsWrapperProps } from './text-wrapper'
import * as React from 'react'

export type BannerColors = 'neutral' | 'informative' | 'danger' | 'warning' | 'success'

export type { ActionsWrapperProps, ContentWrapperProps, SupportingTextProps, TextProps, TextsWrapperProps }

export const iconMapping: Record<BannerColors, IconGlyph> = {
  neutral: 'InformationInCircle',
  informative: 'InformationInCircle',
  danger: 'ExclamationInCircle',
  warning: 'ExclamationInTriangle',
  success: 'CheckMarkInCircle',
}

export interface BannerProps {
  /** Color theme of the banner */
  color: BannerColors
  /**
   * Visual display style. Use 'overlay' for a shadowed, elevated appearance.
   * @default 'inline'
   */
  display?: 'inline' | 'overlay'
  /**
   * Main message text of the banner.
   * @deprecated Use compound components (Banner.TextsWrapper + Banner.Text) instead.
   */
  text?: string
  /**
   * Supporting text displayed below the main text. Rendered as sanitized HTML.
   * @deprecated Use compound components (Banner.TextsWrapper + Banner.SupportingText) instead.
   */
  supportingText?: string
  /**
   * Supporting text as an arbitrary React node.
   * @deprecated Use compound components instead.
   */
  supportingTextHtml?: React.ReactNode
  /** Size of the banner */
  size: 'M' | 'S'
  /** Whether to show the default status icon */
  hasIcon?: boolean
  className?: string
  /**
   * Whether to vertically center the content inside the banner.
   * @default false
   */
  contentIsCentered?: boolean
  /** Position of the action buttons relative to the text */
  actionsPosition?: ContentWrapperProps['actionsPosition']
  /**
   * Compound sub-component children.
   * When provided, the default text/supportingText shorthand rendering is skipped.
   */
  children?: React.ReactNode
  /**
   * Custom icon glyph. Only supported for 'neutral' and 'informative' colors.
   * Falls back to the default icon for other colors.
   */
  icon?: IconGlyph
}

export interface BannerSubComponents {
  ActionsWrapper: typeof ActionsWrapper,
  TextsWrapper: typeof TextsWrapper,
  ContentWrapper: typeof ContentWrapper,
  Text: typeof Text,
  SupportingText: typeof SupportingText
}
